import {
    EMPTY_NOTE_WORKFLOWS_DOCUMENT,
    MainNodeKind,
    NoteWorkflow,
    NoteWorkflowsDocument,
    OutcomeNode,
    StepNode,
    Subflow,
    SubflowNodeKind,
    WorkflowOutputConfiguration
} from "./noteWorkflowsTypes";
import { clampDescription, clampTitle, clampTriggerDescription, workflowOutputConfigurationSchema } from "./noteWorkflowsSchema";
import type { BoxTaskSettings } from "./boxTaskTypes";
import { emptyTaskSettings } from "./boxTaskTypes";

type EditableNodeFields = { title?: string; description?: string; task_settings?: BoxTaskSettings } & WorkflowOutputConfiguration;

function applyOutputConfiguration(node: WorkflowOutputConfiguration, fields: WorkflowOutputConfiguration): void {
    if (fields.output_type === undefined && fields.options === undefined && fields.context_selectors === undefined && fields.analytics === undefined && fields.client_brain_action === undefined) return;
    const configuration = workflowOutputConfigurationSchema.parse({
        output_type: fields.output_type ?? node.output_type,
        options: fields.options ?? node.options,
        context_selectors: fields.context_selectors ?? node.context_selectors,
        analytics: fields.analytics !== undefined ? fields.analytics : node.analytics,
        client_brain_action: fields.client_brain_action !== undefined ? fields.client_brain_action : node.client_brain_action
    });
    node.output_type = configuration.output_type;
    node.options = configuration.options?.map(option => ({ ...option }));
    node.context_selectors = configuration.context_selectors?.slice();
    node.analytics = configuration.analytics ? structuredClone(configuration.analytics) : configuration.analytics;
    node.client_brain_action = configuration.client_brain_action ? structuredClone(configuration.client_brain_action) : configuration.client_brain_action;
}

function newId(prefix: string): string {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
        return `${prefix}-${crypto.randomUUID()}`;
    }
    return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function cloneDocument(document: NoteWorkflowsDocument): NoteWorkflowsDocument {
    return JSON.parse(JSON.stringify(document)) as NoteWorkflowsDocument;
}

export function documentsEqual(left: NoteWorkflowsDocument, right: NoteWorkflowsDocument): boolean {
    return JSON.stringify(left) === JSON.stringify(right);
}

function findWorkflow(document: NoteWorkflowsDocument, workflowId: string): NoteWorkflow | undefined {
    return document.workflows.find(workflow => workflow.id === workflowId);
}

function findSubflow(workflow: NoteWorkflow, subflowId: string): Subflow | undefined {
    return (workflow.subflows ?? []).find(subflow => subflow.id === subflowId);
}

function ensureSubflows(workflow: NoteWorkflow): Subflow[] {
    if (!workflow.subflows) {
        workflow.subflows = [];
    }
    return workflow.subflows;
}

export function addWorkflow(document: NoteWorkflowsDocument): {
    document: NoteWorkflowsDocument;
    workflow: NoteWorkflow;
} {
    const draft = cloneDocument(document);

    const workflow: NoteWorkflow = {
        id: newId("wf"),
        view_all_boxes: false,
        trigger: { id: newId("tr"), title: "", description: "" },
        steps: [],
        outcomes: [],
        subflows: []
    };
    draft.workflows.push(workflow);
    return { document: draft, workflow };
}

export function updateWorkflowViewAllBoxes(
    document: NoteWorkflowsDocument,
    workflowId: string,
    enabled: boolean
): NoteWorkflowsDocument {
    const draft = cloneDocument(document);
    const workflow = findWorkflow(draft, workflowId);
    if (!workflow) {
        throw new Error(`Workflow not found: ${workflowId}`);
    }
    workflow.view_all_boxes = enabled;
    return draft;
}

export function addStep(
    document: NoteWorkflowsDocument,
    workflowId: string
): { document: NoteWorkflowsDocument; step: StepNode } {
    const draft = cloneDocument(document);
    const workflow = findWorkflow(draft, workflowId);
    if (!workflow) {
        throw new Error(`Workflow not found: ${workflowId}`);
    }

    const step: StepNode = { id: newId("st"), title: "", description: "", display_mode: "content", task_settings: emptyTaskSettings() };
    workflow.steps.push(step);
    return { document: draft, step };
}

export function addOutcome(
    document: NoteWorkflowsDocument,
    workflowId: string
): { document: NoteWorkflowsDocument; outcome: OutcomeNode } {
    const draft = cloneDocument(document);
    const workflow = findWorkflow(draft, workflowId);
    if (!workflow) {
        throw new Error(`Workflow not found: ${workflowId}`);
    }
    if (workflow.steps.length === 0) {
        throw new Error("Workflow must have at least one step before adding outcomes");
    }

    const outcome: OutcomeNode = { id: newId("oc"), title: "", description: "", display_mode: "content", task_settings: emptyTaskSettings() };
    workflow.outcomes.push(outcome);
    return { document: draft, outcome };
}

export function updateNode(
    document: NoteWorkflowsDocument,
    workflowId: string,
    nodeKind: MainNodeKind,
    nodeId: string,
    fields: EditableNodeFields
): NoteWorkflowsDocument {
    const draft = cloneDocument(document);
    const workflow = findWorkflow(draft, workflowId);
    if (!workflow) {
        throw new Error(`Workflow not found: ${workflowId}`);
    }

    const applyUpdates = (node: { title?: string; description: string; task_settings?: BoxTaskSettings } & WorkflowOutputConfiguration) => {
        if (nodeKind === "trigger" && (fields.output_type !== undefined || fields.options !== undefined || fields.context_selectors !== undefined || fields.analytics !== undefined || fields.client_brain_action !== undefined)) {
            throw new Error("Triggers cannot configure output options or project sources");
        }
        if (nodeKind !== "trigger") applyOutputConfiguration(node, fields);
        if (fields.task_settings !== undefined && nodeKind !== "trigger") node.task_settings = fields.task_settings;
        if (fields.title !== undefined) {
            node.title = clampTitle(fields.title.trim());
        }
        if (fields.description !== undefined) {
            node.description = (nodeKind === "trigger" ? clampTriggerDescription : clampDescription)(fields.description.trim());
        }
    };

    if (nodeKind === "trigger") {
        if (workflow.trigger.id !== nodeId) {
            throw new Error(`Trigger not found: ${nodeId}`);
        }
        applyUpdates(workflow.trigger);
        return draft;
    }

    if (nodeKind === "step") {
        const step = workflow.steps.find(item => item.id === nodeId);
        if (!step) {
            throw new Error(`Step not found: ${nodeId}`);
        }
        applyUpdates(step);
        return draft;
    }

    if (nodeKind === "outcome") {
        const outcome = workflow.outcomes.find(item => item.id === nodeId);
        if (!outcome) {
            throw new Error(`Outcome not found: ${nodeId}`);
        }
        applyUpdates(outcome);
        return draft;
    }

    throw new Error(`Unknown node kind: ${nodeKind}`);
}

export function updateNodeDescription(
    document: NoteWorkflowsDocument,
    workflowId: string,
    nodeKind: MainNodeKind,
    nodeId: string,
    description: string
): NoteWorkflowsDocument {
    return updateNode(document, workflowId, nodeKind, nodeId, { description });
}

export function deleteNode(
    document: NoteWorkflowsDocument,
    workflowId: string,
    nodeKind: MainNodeKind,
    nodeId: string
): NoteWorkflowsDocument {
    const draft = cloneDocument(document);
    const workflow = findWorkflow(draft, workflowId);
    if (!workflow) {
        throw new Error(`Workflow not found: ${workflowId}`);
    }

    if (nodeKind === "trigger") {
        if (workflow.trigger.id !== nodeId) {
            throw new Error(`Trigger not found: ${nodeId}`);
        }
        draft.workflows = draft.workflows.filter(item => item.id !== workflowId);
        return draft;
    }

    if (nodeKind === "step") {
        const index = workflow.steps.findIndex(step => step.id === nodeId);
        if (index === -1) {
            throw new Error(`Step not found: ${nodeId}`);
        }
        workflow.steps.splice(index, 1);
        return draft;
    }

    if (nodeKind === "outcome") {
        workflow.outcomes = workflow.outcomes.filter(outcome => outcome.id !== nodeId);
        return draft;
    }

    throw new Error(`Unknown node kind: ${nodeKind}`);
}

export function addSubflow(
    document: NoteWorkflowsDocument,
    workflowId: string
): { document: NoteWorkflowsDocument; subflow: Subflow; step: StepNode } {
    const draft = cloneDocument(document);
    const workflow = findWorkflow(draft, workflowId);
    if (!workflow) {
        throw new Error(`Workflow not found: ${workflowId}`);
    }
    const subflows = ensureSubflows(workflow);

    const step: StepNode = { id: newId("st"), title: "", description: "", task_settings: emptyTaskSettings() };
    const subflow: Subflow = { id: newId("sf"), title: "", description: "", display_mode: "content", task_settings: emptyTaskSettings(), steps: [step] };
    subflows.push(subflow);
    return { document: draft, subflow, step };
}

export function addSubflowStep(
    document: NoteWorkflowsDocument,
    workflowId: string,
    subflowId: string
): { document: NoteWorkflowsDocument; step: StepNode } {
    const draft = cloneDocument(document);
    const workflow = findWorkflow(draft, workflowId);
    if (!workflow) {
        throw new Error(`Workflow not found: ${workflowId}`);
    }
    const subflow = findSubflow(workflow, subflowId);
    if (!subflow) {
        throw new Error(`Subflow not found: ${subflowId}`);
    }

    const step: StepNode = { id: newId("st"), title: "", description: "", display_mode: "content", task_settings: emptyTaskSettings() };
    subflow.steps.push(step);
    return { document: draft, step };
}

export function updateSubflowNode(
    document: NoteWorkflowsDocument,
    workflowId: string,
    nodeKind: SubflowNodeKind,
    nodeId: string,
    fields: EditableNodeFields,
    subflowId?: string
): NoteWorkflowsDocument {
    const draft = cloneDocument(document);
    const workflow = findWorkflow(draft, workflowId);
    if (!workflow) {
        throw new Error(`Workflow not found: ${workflowId}`);
    }

    const applyUpdates = (node: { title?: string; description: string; task_settings?: BoxTaskSettings } & WorkflowOutputConfiguration) => {
        applyOutputConfiguration(node, fields);
        if (fields.task_settings !== undefined) node.task_settings = fields.task_settings;
        if (fields.title !== undefined) {
            node.title = clampTitle(fields.title.trim());
        }
        if (fields.description !== undefined) {
            node.description = clampDescription(fields.description.trim());
        }
    };

    if (nodeKind === "subflow") {
        const subflow = findSubflow(workflow, nodeId);
        if (!subflow) {
            throw new Error(`Subflow not found: ${nodeId}`);
        }
        applyUpdates(subflow);
        return draft;
    }

    if (nodeKind === "subflow_step") {
        if (!subflowId) {
            throw new Error("subflowId is required for subflow_step updates");
        }
        const subflow = findSubflow(workflow, subflowId);
        if (!subflow) {
            throw new Error(`Subflow not found: ${subflowId}`);
        }
        const step = subflow.steps.find(item => item.id === nodeId);
        if (!step) {
            throw new Error(`Subflow step not found: ${nodeId}`);
        }
        applyUpdates(step);
        return draft;
    }

    throw new Error(`Unknown subflow node kind: ${nodeKind}`);
}

export function deleteSubflow(
    document: NoteWorkflowsDocument,
    workflowId: string,
    subflowId: string
): NoteWorkflowsDocument {
    const draft = cloneDocument(document);
    const workflow = findWorkflow(draft, workflowId);
    if (!workflow) {
        throw new Error(`Workflow not found: ${workflowId}`);
    }
    workflow.subflows = (workflow.subflows ?? []).filter(item => item.id !== subflowId);
    return draft;
}

export function deleteSubflowStep(
    document: NoteWorkflowsDocument,
    workflowId: string,
    subflowId: string,
    stepId: string
): NoteWorkflowsDocument {
    const draft = cloneDocument(document);
    const workflow = findWorkflow(draft, workflowId);
    if (!workflow) {
        throw new Error(`Workflow not found: ${workflowId}`);
    }
    const subflow = findSubflow(workflow, subflowId);
    if (!subflow) {
        throw new Error(`Subflow not found: ${subflowId}`);
    }
    subflow.steps = subflow.steps.filter(step => step.id !== stepId);
    return draft;
}

export function normalizeDocument(data: unknown): NoteWorkflowsDocument {
    if (!data || typeof data !== "object") {
        return cloneDocument(EMPTY_NOTE_WORKFLOWS_DOCUMENT);
    }
    const raw = data as Partial<NoteWorkflowsDocument>;
    const workflows = Array.isArray(raw.workflows)
        ? raw.workflows.map(workflow => ({
              ...workflow,
              steps: workflow.steps.map(step => ({
                  ...step,
                  display_mode: "content" as const
              })),
              outcomes: workflow.outcomes.map(outcome => ({
                  ...outcome,
                  display_mode: "content" as const
              })),
              subflows: Array.isArray(workflow.subflows)
                  ? workflow.subflows.map(subflow => ({
                        ...subflow,
                        display_mode: "content" as const,
                        steps: subflow.steps.map(step => ({
                            ...step,
                            display_mode: "content" as const
                        }))
                    }))
                  : []
          }))
        : [];
    return {
        schema_version: typeof raw.schema_version === "number" ? raw.schema_version : 1,
        workflows,
        updated_at: typeof raw.updated_at === "number" ? raw.updated_at : undefined,
        updated_by: typeof raw.updated_by === "string" ? raw.updated_by : undefined
    };
}
